'use client';
import {
    MotionMaybe,
    normalizeAnimationPreset,
    useMotionControls,
} from '@/utils/animation';
import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { useInView, useMotionValue, useSpring } from 'framer-motion';
import { useEffect, useRef } from 'react';
import IconValue from './atoms/IconValue';
import { MarkDown } from './atoms/MarkDown';

function parseNumericValue(val) {
    if (typeof val === 'number' && Number.isFinite(val)) return val;
    const stripped = String(val ?? '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/,/g, ' ')
        .trim();
    const match = stripped.match(/-?\d*\.?\d+/);
    return match ? parseFloat(match[0]) : 0;
}

function extractNumberMeta(val) {
    const raw = String(val ?? '');
    const hasBold =
        /\*\*(?!\s)([^*]+?)\*\*/s.test(raw) ||
        /__(?!\s)([^_]+?)__/s.test(raw) ||
        /<(?:b|strong)\b[^>]*>/i.test(raw);
    const hasItalic =
        /(^|[^*])\*(?!\*)([^*]+?)\*(?!\*)/s.test(raw) ||
        /(^|[^_])_(?!_)([^_]+?)_(?!_)/s.test(raw) ||
        /<(?:i|em)\b[^>]*>/i.test(raw);
    const hasUnderline = /<(?:u)\b[^>]*>/i.test(raw);
    const tagMatch = raw.match(/<[^>]+>/);
    const styleMatch = tagMatch?.[0]?.match(/style=["']([^"']*)["']/i);
    const classMatch = tagMatch?.[0]?.match(/class=["']([^"']*)["']/i);
    return {
        numeric: parseNumericValue(raw),
        style: styleMatch?.[1],
        className: classMatch?.[1],
        bold: hasBold,
        italic: hasItalic,
        underline: hasUnderline,
    };
}

function styleStringToObject(str = '') {
    if (!str) return {};
    if (typeof str === 'object') return { ...str };
    const safe = typeof str === 'string' ? str : String(str || '');
    return safe.split(';').reduce((acc, decl) => {
        const [prop, ...rest] = decl.split(':');
        if (!prop || rest.length === 0) return acc;
        const key = prop.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
        const value = rest.join(':').trim();
        if (key && value) acc[key] = value;
        return acc;
    }, {});
}

export default function ItemGrid(props) {
    const {
        Tagline,
        Header,
        Features = [],
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        SecondaryButtonLink,
        OutlineButtonText,
        OutlineButtonLink,
        ColorScheme = 'light',
        VerticalSpacing = 'medium',
        Animation = 'none',
        Duration = 0.6,
        AnimationGroup = 'content',
        EditorPreview = false,
    } = props;

    const {
        backgroundColor,
        headerColor,
        thirdButton,
        secondaryButton,
        taglineColor,
        textColor,
        iconColor,
        boxColor,
        boxBorder,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const vSpace = getVerticalSpacing(VerticalSpacing);
    const normalizedGroup = (() => {
        const raw = String(AnimationGroup ?? '')
            .toLowerCase()
            .trim();
        if (['cta', 'ctas', 'button', 'buttons'].includes(raw)) return 'ctas';
        if (['feature', 'features', 'cards', 'items'].includes(raw))
            return 'features';
        if (raw === 'all') return 'all';
        return 'content';
    })();
    const presetGroup =
        normalizedGroup === 'features' || normalizedGroup === 'all'
            ? 'features'
            : 'content';
    const normalizedPreset = normalizeAnimationPreset(Animation, presetGroup);
    const {
        enableAnim,
        isStagger,
        childMotionProps,
        containerMotionProps,
        remountKey,
    } = useMotionControls({
        preset: normalizedPreset,
        duration: Number(Duration) || 0.6,
        editorPreview: EditorPreview,
        group: normalizedGroup === 'all' ? 'content' : normalizedGroup,
        keyBase: 'item-counter',
    });
    const animateContent =
        enableAnim &&
        (normalizedGroup === 'all' || normalizedGroup === 'content');
    const animateFeatures =
        enableAnim &&
        (normalizedGroup === 'all' || normalizedGroup === 'features');
    const animateCTAs =
        enableAnim && (normalizedGroup === 'all' || normalizedGroup === 'ctas');

    // Use a purer zoom (no slide) to match Process; strip child whileInView when cascading.
    const featureMotionBase = (() => {
        if (normalizedPreset === 'zoom') {
            const zoomDur = Math.max(0.2, Number(Duration) || 0.6);
            return {
                ...childMotionProps,
                variants: {
                    offscreen: { opacity: 0, scale: 0.96 },
                    onscreen: {
                        opacity: 1,
                        scale: 1,
                        transition: { type: 'tween', duration: zoomDur },
                    },
                },
            };
        }
        return childMotionProps;
    })();

    // For cascade, use Process-like child variants (opacity + slight lift) so the stagger is visible.
    const featureChildMotionProps =
        isStagger && animateFeatures
            ? {
                  variants: {
                      hidden: { opacity: 0, y: 16 },
                      show: {
                          opacity: 1,
                          y: 0,
                          transition: {
                              duration: Math.max(
                                  0.4,
                                  (Number(Duration) || 0.6) - 0.2
                              ),
                          },
                      },
                  },
              }
            : featureMotionBase;
    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign: 'justify-center',
            wrapSpacing: 'mt-10',
            className: 'relative z-40',
            buttonsClassName:
                'flex flex-wrap items-center justify-center gap-4',
            enableAnim: animateCTAs,
            motionProps: childMotionProps,
        }
    );

    return (
        <section
            className={`editable-component relative ${backgroundColor} ${vSpace}`}
            style={backgroundStyle}
        >
            <div className="relative z-40 mx-auto flex max-w-7xl flex-col items-center px-6 lg:px-8">
                {Header && (
                    <MotionMaybe
                        enable={animateContent}
                        nodeKey={`${remountKey}-header`}
                        motionProps={childMotionProps}
                        className="mx-auto mb-8 max-w-3xl text-center"
                    >
                        <div className="mb-6  md:mx-auto md:mb-10 ">
                            <div>
                                <MarkDown
                                    className={`font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                                >
                                    {Tagline}
                                </MarkDown>
                            </div>

                            <MarkDown
                                variant="h2"
                                className={`max-w-2xl font-display text-2xl tracking-tight ${headerColor} md:text-3xl lg:text-4xl`}
                            >
                                {Header}
                            </MarkDown>
                        </div>
                    </MotionMaybe>
                )}

                <MotionMaybe
                    enable={animateFeatures && isStagger}
                    nodeKey={`${remountKey}-grid`}
                    motionProps={containerMotionProps}
                    className="flex flex-wrap items-stretch justify-center gap-6 text-center md:gap-8"
                >
                    {Features.map((Feature, index) => {
                        // Extract numeric value and any class/style overrides from markdown
                        const numberMeta = extractNumberMeta(Feature.Numbers);
                        const iconClass = (Feature.Icon || '').trim();
                        const numberStyleRaw = numberMeta.style
                            ? styleStringToObject(numberMeta.style)
                            : undefined;
                        const numberStyle = filterTextStyle(numberStyleRaw);
                        const hasCustomNumberStyle =
                            Boolean(numberStyle) ||
                            Boolean((numberMeta.className || '').trim());
                        const hasGradient =
                            Boolean(numberStyle?.background) ||
                            Boolean(numberStyle?.backgroundImage);
                        const gradientInfo = hasGradient
                            ? parseGradientInfo(
                                  numberStyle?.backgroundImage ||
                                      numberStyle?.background ||
                                      ''
                              )
                            : null;
                        const {
                            background,
                            backgroundImage,
                            backgroundSize,
                            backgroundPosition,
                            backgroundRepeat,
                            ...styleSansBackground
                        } = numberStyle || {};
                        // Apply gradient directly to glyphs (number + icon) with clip; avoid wrapper fills.
                        const numberStyleOverride = hasGradient
                            ? {
                                  ...styleSansBackground,
                                  background: 'transparent',
                                  backgroundImage:
                                      backgroundImage ||
                                      background ||
                                      undefined,
                                  backgroundSize,
                                  backgroundPosition,
                                  backgroundRepeat,
                                  WebkitBackgroundClip: 'text',
                                  backgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  color: 'transparent',
                                  display: 'inline-block',
                              }
                            : numberStyle;
                        const gradientEndColor = hasGradient
                            ? getFinalGradientColor(
                                  backgroundImage || background || ''
                              )
                            : null;
                        const iconGradientColor =
                            gradientInfo && gradientInfo.colors.length
                                ? getIconEdgeColor(gradientInfo)
                                : gradientEndColor;
                        const iconStyleOverride = hasGradient
                            ? {
                                  ...styleSansBackground,
                                  color: iconGradientColor,
                              }
                            : numberStyle;
                        const sharedColorClass = hasGradient ? '' : iconColor;
                        const itemMotionProps = featureChildMotionProps;

                        return (
                            <MotionMaybe
                                key={Feature.Title || index}
                                enable={animateFeatures}
                                nodeKey={`${remountKey}-feature-${index}`}
                                motionProps={itemMotionProps}
                                className="duration-20 flex w-[240px] min-w-0 flex-none flex-col self-stretch transition-all md:w-[260px]"
                            >
                                <div
                                    className={`${boxColor} ${boxBorder} flex min-w-0 flex-1 flex-col rounded-[min(1.5rem,var(--shape-radius))] p-5 lg:p-6`}
                                >
                                    <div
                                        className={`${iconColor} text-base leading-4`}
                                    >
                                        <div className="items-bottom mb-3 flex h-16 items-center justify-center gap-2 bg-transparent">
                                            <Counter
                                                value={numberMeta.numeric}
                                                counterColor={
                                                    hasCustomNumberStyle ||
                                                    hasGradient
                                                        ? ''
                                                        : iconColor
                                                }
                                                editorPreview={EditorPreview}
                                                formatting={numberMeta}
                                                classNameOverride={`${
                                                    numberMeta.className || ''
                                                }`}
                                                styleOverride={
                                                    numberStyleOverride
                                                }
                                            />
                                            {iconClass && (
                                                <IconValue
                                                    value={iconClass}
                                                    className={`text-xl lg:text-xl ${sharedColorClass} ${
                                                        numberMeta.className ||
                                                        ''
                                                    }`}
                                                    style={iconStyleOverride}
                                                />
                                            )}
                                        </div>
                                        <MarkDown
                                            variant="h3"
                                            className="text-xl font-text leading-6"
                                        >
                                            {Feature.Title}
                                        </MarkDown>
                                    </div>
                                    <div
                                        className={`${textColor} mt-3 flex flex-auto flex-col break-words text-xs font-text leading-4 md:text-base md:leading-7`}
                                    >
                                        <MarkDown className="flex-auto break-words">
                                            {Feature.Description}
                                        </MarkDown>
                                    </div>
                                </div>
                            </MotionMaybe>
                        );
                    })}
                </MotionMaybe>

                {hasCTAs && (
                    <div className="w-full">
                        <MotionMaybe
                            enable={animateCTAs}
                            nodeKey={`${remountKey}-ctas`}
                            motionProps={childMotionProps}
                        >
                            {CTAs}
                        </MotionMaybe>
                    </div>
                )}
            </div>
        </section>
    );
}

// Limit inline styles to text-related props to avoid blocky gradient artifacts
function filterTextStyle(styleObj = {}) {
    const allowed = new Set([
        'background',
        'backgroundImage',
        'backgroundSize',
        'backgroundPosition',
        'backgroundRepeat',
        'color',
        'fontWeight',
        'fontStyle',
        'textDecoration',
        'textTransform',
        'letterSpacing',
        'lineHeight',
        'fontSize',
        'fontFamily',
    ]);
    const out = {};
    for (const [k, v] of Object.entries(styleObj || {})) {
        if (allowed.has(k)) out[k] = v;
    }
    return out;
}

function getLastGradientColor(str = '') {
    const raw = String(str || '');
    const lastPart = raw.split(',').pop() || '';
    const cleaned = lastPart.replace(/\)\s*$/, '').trim();
    const match =
        cleaned.match(
            /(#(?:[0-9a-f]{3,8})|\b(?:rgba?|hsla?)\([^)]*\)|[a-z]+)$/i
        ) || [];
    return match[1] || undefined;
}

function getFinalGradientColor(str = '') {
    const info = parseGradientInfo(str);
    if (info?.colors?.length) {
        return info.colors[info.colors.length - 1] || info.colors[0];
    }
    return getLastGradientColor(str);
}

function getIconEdgeColor(info) {
    if (!info || !info.colors.length) return null;
    const a = ((info.angle % 360) + 360) % 360;
    // CSS angles: 0=top, 90=right, 180=bottom, 270=left.
    // Convert to standard trig (0=east) to inspect x-component.
    const rad = ((a - 90) * Math.PI) / 180;
    const cosX = Math.cos(rad);
    const first = info.colors[0];
    const last = info.colors[info.colors.length - 1];
    // If x-component is negative (points left), the right edge sees the FIRST stop.
    // If x-component is positive (points right), the right edge sees the LAST stop.
    if (cosX < 0) return first || last;
    return last || first;
}

function parseGradientInfo(str = '') {
    const raw = String(str || '');
    const match = raw.match(
        /linear-gradient\(\s*([-\d.]+)deg\s*,\s*([\s\S]+)\)/i
    );
    if (!match) return null;
    const angle = parseFloat(match[1]) || 0;
    const stopsStr = match[2] || '';
    const colorRegex =
        /(#[0-9a-fA-F]{3,8}|(?:rgba?|hsla?)\([^)]*\)|[a-zA-Z]+)(?:\s+\d+%?)?/g;
    const colors = [];
    let m;
    while ((m = colorRegex.exec(stopsStr))) {
        colors.push(m[1]);
    }
    if (!colors.length) return null;
    return { angle, colors };
}

export function Counter({
    value,
    direction = 'up',
    iconColor,
    editorPreview,
    formatting,
    classNameOverride,
    styleOverride,
}) {
    const ref = useRef(null);
    const safeValue = parseNumericValue(value);
    const motionValue = useMotionValue(direction === 'down' ? safeValue : 0);
    const springValue = useSpring(motionValue, {
        damping: 100,
        stiffness: 100,
    });
    const isInView = useInView(ref, { once: true, margin: '-100px' });

    // Safely parse the value to a number. If it's not a valid number, fallback to 0.
    const numericValue = safeValue;

    useEffect(() => {
        // In the editor preview, force the animation to run immediately
        if (isInView || editorPreview) {
            motionValue.set(direction === 'down' ? 0 : numericValue);
        }
    }, [motionValue, isInView, numericValue, editorPreview]);

    useEffect(() =>
        springValue.on('change', (latest) => {
            if (ref.current) {
                ref.current.textContent = Intl.NumberFormat('en-US').format(
                    latest.toFixed(0)
                );
            }
        })
    );

    const baseStyle = styleOverride
        ? styleStringToObject(styleOverride)
        : undefined;
    const formattedStyle =
        formatting &&
        (formatting.bold || formatting.italic || formatting.underline)
            ? {
                  ...(baseStyle || {}),
                  ...(formatting.bold ? { fontWeight: 800 } : {}),
                  ...(formatting.italic ? { fontStyle: 'italic' } : {}),
                  ...(formatting.underline
                      ? { textDecoration: 'underline' }
                      : {}),
              }
            : baseStyle;

    return (
        <span
            ref={ref}
            className={`font-display text-4xl font-normal lg:text-5xl ${iconColor} ${
                classNameOverride || ''
            }`}
            style={formattedStyle}
        >
            {Intl.NumberFormat('en-US').format(numericValue)}
        </span>
    );
}
